import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
    UserPlus, Mail, Lock, User, CheckCircle, AlertCircle, Loader2, 
    Users, Map, FileText, Shield, ShieldCheck
} from 'lucide-react';

const AddEditorPage = () => {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    // Form State
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        password: '',
        role: 'editor'
    });

    // Data State
    const [editors, setEditors] = useState([]);
    
    // UI State
    const [loading, setLoading] = useState(false); // Kayıt butonu için
    const [dataLoading, setDataLoading] = useState(true); // Tablo yüklenmesi için
    const [message, setMessage] = useState({ type: '', text: '' });

    // Component Mount Olduğunda Verileri Çek
    useEffect(() => {
        fetchEditors();
    }, []);

    const fetchEditors = async () => {
        const token = localStorage.getItem('token');
        // Token yoksa login'e atabilirsin veya boş döndürebilirsin
        if (!token) {
            console.warn("Token bulunamadı");
            // navigate('/login'); // İstersen login'e yönlendir
            return;
        }

        try {
            // Backend endpoint'imiz: /api/admin/editors
            const response = await axios.get(`${API_URL}/editors`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            // Backend'den gelen veri yapısı: [{id: 1, stats: {total_routes: 5, ...}, ...}]
            setEditors(response.data);
        } catch (error) {
            console.error("Editör listesi çekilemedi", error);
            setMessage({ type: 'error', text: 'Editör listesi yüklenirken hata oluştu.' });
        } finally {
            setDataLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        const token = localStorage.getItem('token');

        try {
            await axios.post(
                `${API_URL}/api/admin/editors/register`, 
                formData, // JSON body olarak gönderiyoruz
                {
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json' 
                    }
                }
            );

            setMessage({ type: 'success', text: 'Yeni kullanıcı başarıyla oluşturuldu!' });
            setFormData({ full_name: '', email: '', password: '', role: 'editor' });
            
            // Listeyi yenile ki yeni eklenen kişi ve boş istatistikleri (0) görünsün
            fetchEditors();

        } catch (error) {
            const errorMsg = error.response?.data?.detail || 'Bir hata oluştu.';
            setMessage({ type: 'error', text: errorMsg });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 text-gray-100 p-6 md:p-12 font-sans">
            <div className="max-w-7xl mx-auto">
                
                {/* Header */}
                <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-800 pb-6">
                    <div>
                        <h1 className="text-3xl font-extrabold text-white tracking-tight">
                            Kullanıcı Yönetimi
                        </h1>
                        <p className="text-gray-400 mt-2 text-sm">
                            Sisteme yeni editörler ekleyin ve performanslarını takip edin.
                        </p>
                    </div>
                    <button 
                        onClick={() => navigate('/admin/dashboard')} 
                        className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-medium border border-gray-700 transition-colors text-white"
                    >
                        Panele Dön
                    </button>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    
                    {/* --- SOL KOLON: Yeni Ekleme Formu --- */}
                    <div className="lg:col-span-1">
                        <div className="bg-gray-800 p-6 rounded-2xl border border-gray-700 shadow-xl sticky top-6">
                            <div className="flex items-center gap-3 mb-6 border-b border-gray-700 pb-4">
                                <div className="p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
                                    <UserPlus className="text-indigo-400 w-5 h-5" />
                                </div>
                                <h2 className="font-bold text-lg text-white">Yeni Kullanıcı Ekle</h2>
                            </div>

                            {message.text && (
                                <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 text-sm border ${
                                    message.type === 'success' 
                                        ? 'bg-green-500/10 border-green-500/20 text-green-400' 
                                        : 'bg-red-500/10 border-red-500/20 text-red-400'
                                }`}>
                                    {message.type === 'success' ? <CheckCircle className="w-5 h-5 shrink-0" /> : <AlertCircle className="w-5 h-5 shrink-0" />}
                                    <span className="leading-5">{message.text}</span>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Ad Soyad</label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                                        <input
                                            type="text"
                                            name="full_name"
                                            value={formData.full_name}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-2.5 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-white text-sm placeholder-gray-600 transition-all"
                                            placeholder="Ad Soyad giriniz"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">E-Posta</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="w-full pl-10 pr-4 py-2.5 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-white text-sm placeholder-gray-600 transition-all"
                                            placeholder="ornek@mekanname.com"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Şifre</label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                                            <input
                                                type="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                required
                                                className="w-full pl-10 pr-4 py-2.5 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-white text-sm placeholder-gray-600 transition-all"
                                                placeholder="••••••"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Rol</label>
                                        <select
                                            name="role"
                                            value={formData.role}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 text-white text-sm cursor-pointer"
                                        >
                                            <option value="editor">Editör</option>
                                            <option value="admin">Yönetici</option>
                                        </select>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-sm text-white transition-all shadow-lg mt-4
                                        ${loading 
                                            ? 'bg-indigo-900/50 cursor-not-allowed' 
                                            : 'bg-indigo-600 hover:bg-indigo-500 hover:shadow-indigo-500/25 active:scale-[0.98]'
                                        }`}
                                >
                                    {loading ? <Loader2 className="animate-spin h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
                                    {loading ? 'Kaydediliyor...' : 'Kullanıcıyı Kaydet'}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* --- SAĞ KOLON: Mevcut Editörler Listesi --- */}
                    <div className="lg:col-span-2">
                        <div className="bg-gray-800 rounded-2xl border border-gray-700 shadow-xl overflow-hidden flex flex-col h-full">
                            <div className="p-6 border-b border-gray-700 flex justify-between items-center bg-gray-800/50">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-teal-500/10 rounded-lg border border-teal-500/20">
                                        <Users className="text-teal-400 w-5 h-5" />
                                    </div>
                                    <h2 className="font-bold text-lg text-white">Mevcut Kullanıcılar</h2>
                                </div>
                                <span className="bg-gray-900 px-3 py-1 rounded-full text-xs font-medium text-gray-400 border border-gray-700">
                                    Toplam: {editors.length}
                                </span>
                            </div>

                            {dataLoading ? (
                                <div className="flex-1 flex flex-col items-center justify-center p-12 text-gray-500">
                                    <Loader2 className="animate-spin h-8 w-8 mb-3 text-indigo-500" />
                                    <span className="text-sm font-medium">Veriler yükleniyor...</span>
                                </div>
                            ) : (
                                <div className="overflow-x-auto flex-1">
                                    <table className="w-full text-left text-sm text-gray-400">
                                        <thead className="bg-gray-900/50 text-gray-500 uppercase font-bold text-xs tracking-wider border-b border-gray-700">
                                            <tr>
                                                <th className="px-6 py-4">Kullanıcı</th>
                                                <th className="px-6 py-4">Yetki</th>
                                                <th className="px-6 py-4 text-center">Performans</th>
                                                <th className="px-6 py-4 text-right">Durum</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-700/50">
                                            {editors.map((editor) => (
                                                <tr key={editor.id} className="hover:bg-gray-700/20 transition-colors group">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            {/* Avatar */}
                                                            <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-white font-bold border border-gray-600 shadow-sm shrink-0">
                                                                {editor.full_name 
                                                                    ? editor.full_name.charAt(0).toUpperCase() 
                                                                    : (editor.email ? editor.email.charAt(0).toUpperCase() : '?')}
                                                            </div>
                                                            <div>
                                                                <div className="font-medium text-gray-200">{editor.full_name || 'İsimsiz'}</div>
                                                                <div className="text-xs text-gray-500">{editor.email}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                                                            editor.role === 'admin' 
                                                                ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' 
                                                                : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                                        }`}>
                                                            {editor.role === 'admin' ? <ShieldCheck size={12}/> : <Shield size={12}/>}
                                                            {editor.role === 'admin' ? 'Yönetici' : 'Editör'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex justify-center gap-6">
                                                            
                                                            {/* Rota İstatistiği */}
                                                            <div className="flex flex-col items-center group/stat" title="Toplam Rota">
                                                                <div className="flex items-center gap-1.5 text-teal-400 bg-teal-400/5 px-2 py-1 rounded-md border border-teal-400/10">
                                                                    <Map className="w-3.5 h-3.5" />
                                                                    <span className="font-bold text-gray-200">
                                                                        {editor.stats?.total_routes || 0}
                                                                    </span>
                                                                </div>
                                                                <span className="text-[10px] uppercase font-bold text-gray-600 mt-1 group-hover/stat:text-teal-400 transition-colors">Rota</span>
                                                            </div>
                                                            
                                                            <div className="w-px bg-gray-700 h-8 self-center opacity-50"></div>
                                                            
                                                            {/* Blog İstatistiği */}
                                                            <div className="flex flex-col items-center group/stat" title="Toplam Blog Yazısı">
                                                                <div className="flex items-center gap-1.5 text-orange-400 bg-orange-400/5 px-2 py-1 rounded-md border border-orange-400/10">
                                                                    <FileText className="w-3.5 h-3.5" />
                                                                    <span className="font-bold text-gray-200">
                                                                        {editor.stats?.total_blogs || 0}
                                                                    </span>
                                                                </div>
                                                                <span className="text-[10px] uppercase font-bold text-gray-600 mt-1 group-hover/stat:text-orange-400 transition-colors">Blog</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="flex items-center justify-end">
                                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider ${
                                                                editor.is_active 
                                                                    ? 'text-green-400' 
                                                                    : 'text-red-400'
                                                            }`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full ${editor.is_active ? 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.5)]' : 'bg-red-400'}`}></span>
                                                                {editor.is_active ? 'Aktif' : 'Pasif'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            
                                            {editors.length === 0 && (
                                                <tr>
                                                    <td colSpan="4" className="px-6 py-12 text-center text-gray-500 italic">
                                                        <div className="flex flex-col items-center gap-3">
                                                            <div className="p-4 bg-gray-800 rounded-full border border-gray-700">
                                                                <Users className="w-6 h-6 opacity-30" />
                                                            </div>
                                                            <span>Henüz kayıtlı kullanıcı bulunamadı.</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddEditorPage;